<template>
  <div class="py-2 w-full ssn-input">
    <div
      class="w-full border border-brand-mid-gray shadow-sm rounded placeholder-brand-mid-gray focus:border-brand-green focus:ring-brand-green flex items-center justify-between py-1 bg-white"
      :class="[
        entered || disabled ? 'bg-brand-light-gray' : '',
        disabled ? 'cursor-not-allowed' : ''
      ]"
    >
      <div class="flex items-center">
        <input
          class="px-4 w-20 border-0 focus:border-transparent focus:ring-0 bg-transparent"
          style="outline: none"
          :type="show ? 'text' : 'password'"
          :disabled="disabled"
          ref="firstInput"
          name="input"
          placeholder="999"
          v-model="first"
          @input="checkProgression('first')"
          @change="updateValue"
          @blur="checkEntered"
          v-maska="'###'"
          :class="{ 'bg-brand-light-gray': disabled || entered, 'text-sm': show }"
          autocomplete="new-password"
          data-private
        />

        <p class="text-xl">-</p>
        <input
          class="px-4 w-16 border-0 focus:border-transparent focus:ring-0 bg-transparent"
          style="outline: none"
          :type="show ? 'text' : 'password'"
          :disabled="disabled"
          ref="midInput"
          name="input"
          placeholder="99"
          v-model="mid"
          @input="checkProgression('mid')"
          @change="updateValue"
          @blur="checkEntered"
          v-maska="'##'"
          :class="{ 'bg-brand-light-gray': disabled || entered, 'text-sm': show }"
          autocomplete="new-password"
          data-private
        />
        <p class="text-xl">-</p>
        <input
          class="px-4 w-24 border-0 focus:border-transparent focus:ring-0 bg-transparent"
          style="outline: none"
          :type="show ? 'text' : 'password'"
          :disabled="disabled"
          ref="lastInput"
          name="input"
          placeholder="9999"
          v-model="last"
          @input="checkProgression('last')"
          @change="updateValue"
          @blur="checkEntered"
          v-maska="'####'"
          :class="{ 'bg-brand-light-gray': disabled || entered, 'text-sm': show }"
          autocomplete="new-password"
          data-private
        />
      </div>
      <div class="flex w-5 h-5 md:w-7 md:h-7 mr-3 justify-center cursor-pointer">
        <img v-if="!show" src="@/assets/icons/show.svg" @click="toggleShow" />
        <img v-else src="@/assets/icons/hide.svg" @click="toggleShow" />
      </div>
    </div>
  </div>
</template>
<script>
export default {
  props: {
    ssn: {
      type: String
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update"],
  mounted() {
    if (this.ssn) {
      this.first = this.ssn.split("-")[0]
      this.mid = this.ssn.split("-")[1]
      this.last = this.ssn.split("-")[2]
    }
  },
  computed: {
    fullSSN() {
      return `${this.first}-${this.mid}-${this.last}`
    },
    validSSN() {
      if (!this.first || !this.mid || !this.last) {
        return false
      }
      return this.first.length === 3 && this.mid.length === 2 && this.last.length === 4
    }
  },
  data() {
    return {
      first: "",
      mid: "",
      last: "",
      entered: false,
      show: false
    }
  },
  methods: {
    focusAndOpenKeyboard(el, timeout) {
      if (!timeout) {
        timeout = 100

        if (el) {
          // Align temp input element approximately where the input element is
          // so the cursor doesn't jump around
          var __tempEl__ = document.createElement("input")
          __tempEl__.style.position = "absolute"
          __tempEl__.style.top = el.offsetTop + 7 + "px"
          __tempEl__.style.left = el.offsetLeft + "px"
          __tempEl__.style.height = 0
          __tempEl__.style.opacity = 0
          // Put this temp element as a child of the page <body> and focus on it
          document.body.appendChild(__tempEl__)
          __tempEl__.focus()

          // The keyboard is open. Now do a delayed focus on the target element
          setTimeout(function () {
            el.focus()
            el.click()
            // Remove the temp element
            document.body.removeChild(__tempEl__)
          }, timeout)
        }
      }
    },
    checkEntered() {
      if (this.first && this.mid && this.last) {
        this.entered = !this.entered
      }
    },
    checkProgression(slot) {
      if (slot === "first" && this.first.length === 3) {
        this.$refs.midInput.focus()

        //iOS requires this
        this.focusAndOpenKeyboard(this.$refs.midInput)
      }
      if (slot === "mid" && this.mid.length === 2) {
        this.$refs.lastInput.focus()

        //iOS requires this
        this.focusAndOpenKeyboard(this.$refs.lastInput)
      }

      if (slot === "last" && this.last.length === 4) {
        this.updateValue()
      }
    },
    updateValue() {
      if (this.validSSN) {
        this.$emit("update", this.fullSSN)
      }
    },
    toggleShow() {
      this.show = !this.show
    }
  }
}
</script>

<style lang="scss" scoped>
@font-face {
  font-family: "asterisk";
  src: url("./../../assets/fonts/asterisk-mask.eot?0000");
  src: url("./../../assets/fonts/asterisk-mask.eot?0000#iefix") format("embedded-opentype"),
    url("./../../assets/fonts/asterisk-mask.woff2?0000") format("woff2"),
    url("./../../assets/fonts/asterisk-mask.woff?0000") format("woff"),
    url("./../../assets/fonts/asterisk-mask.ttf?0000") format("truetype"),
    url("./../../assets/fonts/asterisk-mask.svg?0000#asterisk") format("svg");
  font-weight: normal;
  font-style: normal;
  unicode-range: U+2160 to U+2188;
}

input[type="password"] {
  @apply text-sm;
  font-family: "asterisk";
  font-style: normal;
  font-weight: normal;
  speak: none;

  /* For safety - reset parent styles, that can break glyph codes*/
  font-variant: normal;
  text-transform: none;

  /* Font smoothing. That was taken from TWBS */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  /* Uncomment for 3D effect */
  /* text-shadow: 1px 1px 1px rgba(127, 127, 127, 0.3); */

  /* add spacing to better separate each image */
  letter-spacing: 2px;
  font-size: 14px;
}
input[type="password"]::placeholder {
  /* Chrome, Firefox, Opera, Safari 10.1+ */
  color: #757575;
  opacity: 1; /* Firefox */
}

input[type="password"]:-ms-input-placeholder {
  /* Internet Explorer 10-11 */
  color: #757575;
}

input[type="password"]::-ms-input-placeholder {
  /* Microsoft Edge */
  color: #757575;
}

// @media screen and (max-width: 768px) {
//   input[type="password"] {
//     font-size: 14px;
//   }
// }
</style>
